import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate, useLocation } from 'react-router-dom';
import api from '../api/api';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger, DialogFooter,
} from '../components/ui/dialog';
import {
  Kanban, CreditCard, User as UserIcon, MagnifyingGlass, DownloadSimple, ChatCircle,
  Trash, Plus, Note, ArrowRight, UploadSimple, Robot, Star,
} from '@phosphor-icons/react';
import { toast } from 'sonner';

const COLUMNS = [
  { key: 'pending', label: 'Nuove', color: 'bg-yellow-100 text-yellow-800' },
  { key: 'notified', label: 'Notificate', color: 'bg-blue-100 text-blue-800' },
  { key: 'assigned', label: 'Assegnate', color: 'bg-indigo-100 text-indigo-800' },
  { key: 'in_progress', label: 'In lavorazione', color: 'bg-purple-100 text-purple-800' },
  { key: 'closed', label: 'Chiuse', color: 'bg-sky-100 text-sky-800' },
  { key: 'escalated_to_admin', label: 'Escalate', color: 'bg-red-100 text-red-800' },
];

const STATUS_LABELS = {
  pending: 'In attesa',
  notified: 'Notificata',
  pending_pro: 'In attesa pro',
  assigned: 'Assegnata',
  accepted: 'Accettata',
  in_progress: 'In corso',
  completed: 'Completata',
  reviewed: 'Recensita',
  closed: 'Chiusa',
  closed_by_pro: 'Chiusa dal pro',
  cancelled: 'Annullata',
  escalated_to_admin: 'Escalation admin',
};
const statusLabel = (s) => STATUS_LABELS[s] || s || '—';
const isCompletedStatus = (s) => s === 'completed' || s === 'reviewed';
const completedCount = (sc) => (sc?.completed || 0) + (sc?.reviewed || 0);

const CrmDashboard = ({ section = null }) => {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const q = params.get('q');
    if (q) {
      setSearchQ(q);
      setActiveTab('search');
    }
  }, [location.search]);

  const [board, setBoard] = useState({});
  const knownLeadIdsRef = useRef(null); // Set of request_ids seen; null until first load
  const [payments, setPayments] = useState({ items: [], stats: {} });
  const [searchQ, setSearchQ] = useState('');
  const [activeTab, setActiveTab] = useState(section || 'pipeline');
  useEffect(() => { if (section) setActiveTab(section); }, [section]);
  const [customersList, setCustomersList] = useState([]);
  const [deletingCustomerId, setDeletingCustomerId] = useState(null);
  const [clearingHistory, setClearingHistory] = useState(false);
  const [searchResults, setSearchResults] = useState(null);
  const [selectedUserId, setSelectedUserId] = useState('');
  const [customer, setCustomer] = useState(null);
  const [noteBody, setNoteBody] = useState('');
  const [messages, setMessages] = useState([]);
  const [msgOpen, setMsgOpen] = useState(false);
  const [msgForm, setMsgForm] = useState({ to_user_id: '', subject: '', body_html: '', template: 'generic' });
  const [reqEditor, setReqEditor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dragOverCol, setDragOverCol] = useState(null);
  const [aiMatches, setAiMatches] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [assigning, setAssigning] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [geoFilter, setGeoFilter] = useState({ region: '', province: '', comune: '' });
  const [distFilter, setDistFilter] = useState({ enabled: false, city: '', radius_km: 30 });

  // Preset lat/lng for major Italian cities (fallback without Google Maps).
  // Admin can pick from these OR type freely — free-text will match by name.
  const CITY_COORDS = {
    'milano': { lat: 45.4642, lng: 9.1900 },
    'roma': { lat: 41.9028, lng: 12.4964 },
    'napoli': { lat: 40.8518, lng: 14.2681 },
    'torino': { lat: 45.0703, lng: 7.6869 },
    'palermo': { lat: 38.1157, lng: 13.3615 },
    'genova': { lat: 44.4056, lng: 8.9463 },
    'bologna': { lat: 44.4949, lng: 11.3426 },
    'firenze': { lat: 43.7696, lng: 11.2558 },
    'bari': { lat: 41.1171, lng: 16.8719 },
    'catania': { lat: 37.5079, lng: 15.0830 },
    'venezia': { lat: 45.4408, lng: 12.3155 },
    'verona': { lat: 45.4384, lng: 10.9916 },
    'padova': { lat: 45.4064, lng: 11.8768 },
    'trieste': { lat: 45.6495, lng: 13.7768 },
    'brescia': { lat: 45.5416, lng: 10.2118 },
    'parma': { lat: 44.8015, lng: 10.3279 },
    'modena': { lat: 44.6471, lng: 10.9252 },
    'reggio emilia': { lat: 44.6979, lng: 10.6297 },
    'perugia': { lat: 43.1122, lng: 12.3888 },
    'livorno': { lat: 43.5485, lng: 10.3106 },
  };

  const [proximityPros, setProximityPros] = useState([]); // pro ids within radius

  // Whenever distance filter changes, hit the nearby endpoint to fetch pro ids
  React.useEffect(() => {
    const run = async () => {
      if (!distFilter.enabled || !distFilter.city) { setProximityPros([]); return; }
      const key = distFilter.city.toLowerCase().trim();
      const coord = CITY_COORDS[key];
      if (!coord) { setProximityPros([]); return; }
      try {
        const r = await api.get(`/api/search/nearby?lat=${coord.lat}&lng=${coord.lng}&radius_km=${distFilter.radius_km}&limit=100`);
        setProximityPros((r.data.results || []).map((p) => p.professional_id));
      } catch { setProximityPros([]); }
    };
    run();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [distFilter.enabled, distFilter.city, distFilter.radius_km]);

  const matchDistance = (r) => {
    if (!distFilter.enabled || !distFilter.city) return true;
    const ids = new Set([...(r.selected_professional_ids || []), ...(r.matched_professional_ids || [])]);
    if (ids.size === 0) return false;
    return proximityPros.some((pid) => ids.has(pid));
  };

  const matchGeo = (r) => {
    const norm = (s) => (s || '').toLowerCase().trim();
    if (geoFilter.region && !norm(r.region).includes(norm(geoFilter.region))) return false;
    if (geoFilter.province && !norm(r.province).includes(norm(geoFilter.province))) return false;
    if (geoFilter.comune && !norm(r.comune).includes(norm(geoFilter.comune))) return false;
    if (!matchDistance(r)) return false;
    return true;
  };

  useEffect(() => {
    if (!authLoading && user && user.role !== 'admin') {
      toast.error('Accesso riservato agli admin');
      navigate('/');
      return;
    }
    if (user?.role === 'admin') { fetchAll(); }
  }, [user, authLoading, navigate]);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [boardR, paymentsR, messagesR] = await Promise.all([
        api.get('/api/admin/crm/requests/board'),
        api.get('/api/admin/crm/payments?limit=50'),
        api.get('/api/admin/crm/messages?limit=30'),
      ]);
      setBoard(boardR.data);
      knownLeadIdsRef.current = new Set(Object.values(boardR.data || {}).flat().map((r) => r.request_id));
      setPayments(paymentsR.data);
      setMessages(messagesR.data);
      if (!section || section === 'customers') {
        try {
          const cR = await api.get('/api/admin/crm/customers');
          setCustomersList(cR.data?.customers || []);
        } catch { /* ignore */ }
      }
    } catch (e) { toast.error('Errore caricamento CRM'); }
    setLoading(false);
  };

  // Pipeline Live: silent board refresh every 30s (no spinner, skips while dragging)
  const pipelineVisible = section === 'pipeline' || (!section && activeTab === 'pipeline');
  const draggingRef = useRef(false);
  const refreshBoardSilently = useCallback(async () => {
    if (draggingRef.current) return; // don't disrupt an active drag
    try {
      const r = await api.get('/api/admin/crm/requests/board');
      const newBoard = r.data || {};
      const allLeads = Object.values(newBoard).flat();
      // Detect newly-arrived leads vs the last known set
      if (knownLeadIdsRef.current) {
        const known = knownLeadIdsRef.current;
        const fresh = allLeads.filter((l) => !known.has(l.request_id));
        if (fresh.length > 0) {
          const urgent = fresh.filter((l) => l.is_urgent);
          const normal = fresh.filter((l) => !l.is_urgent);
          if (urgent.length > 0) {
            const ut = urgent.length === 1
              ? `⚡ URGENTE — ${urgent[0].description?.slice(0, 38) || 'nuovo lead'}${urgent[0].region ? ` — ${urgent[0].region}` : ''}`
              : `⚡ ${urgent.length} nuovi lead URGENTI ricevuti`;
            toast.error(ut, {
              description: 'Da gestire con priorità nella Pipeline',
              duration: 10000,
              style: { borderLeft: '4px solid #dc2626' },
            });
          }
          if (normal.length > 0) {
            const label = normal.length === 1
              ? `Nuovo lead: ${normal[0].description?.slice(0, 40) || 'richiesta'}${normal[0].region ? ` — ${normal[0].region}` : ''}`
              : `${normal.length} nuovi lead ricevuti`;
            toast.success(label, {
              description: 'Aggiornato in tempo reale nella Pipeline',
            });
          }
        }
      }
      knownLeadIdsRef.current = new Set(allLeads.map((l) => l.request_id));
      setBoard(newBoard);
    } catch { /* silent */ }
  }, []);

  useEffect(() => {
    if (user?.role !== 'admin' || !pipelineVisible) return;
    const id = setInterval(refreshBoardSilently, 30000);
    return () => clearInterval(id);
  }, [user, pipelineVisible, refreshBoardSilently]);

  const runSearch = useCallback(async () => {
    if (searchQ.trim().length < 2) { setSearchResults(null); return; }
    try {
      const r = await api.get(`/api/admin/crm/search?q=${encodeURIComponent(searchQ.trim())}`);
      setSearchResults(r.data);
    } catch { toast.error('Errore ricerca'); }
  }, [searchQ]);

  useEffect(() => {
    const t = setTimeout(runSearch, 350);
    return () => clearTimeout(t);
  }, [runSearch]);

  const openCustomer = async (uid) => {
    setSelectedUserId(uid);
    try {
      const r = await api.get(`/api/admin/crm/customer/${uid}`);
      setCustomer(r.data);
    } catch { toast.error('Errore caricamento cliente'); }
  };

  const clearCustomerHistory = async () => {
    if (!customer) return;
    const uid = customer.user.user_id;
    if (!window.confirm(`Svuotare lo storico di ${customer.user.name}?\n\nVerranno eliminate richieste, prenotazioni e pagamenti non incassati. L'anagrafica resta attiva. Azione irreversibile.`)) return;
    setClearingHistory(true);
    try {
      const r = await api.delete(`/api/admin/users/${uid}/history`);
      toast.success(`Storico svuotato: ${r.data.requests} richieste, ${r.data.bookings} prenotazioni, ${r.data.payments} pagamenti`);
      openCustomer(uid);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore pulizia storico');
    } finally { setClearingHistory(false); }
  };

  const deleteCustomer = async (c) => {
    const label = c.name || c.email;
    if (!window.confirm(`Eliminare definitivamente il cliente "${label}"?\n\nVerranno cancellati anche le sue ${c.requests_count} richieste, prenotazioni, note e recensioni. L'azione è irreversibile.`)) return;
    setDeletingCustomerId(c.user_id);
    try {
      await api.delete(`/api/admin/users/${c.user_id}`);
      toast.success(`Cliente ${label} eliminato`);
      setCustomersList(prev => prev.filter(x => x.user_id !== c.user_id));
      if (customer?.user?.user_id === c.user_id) setCustomer(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore eliminazione cliente');
    } finally { setDeletingCustomerId(null); }
  };


  const addNote = async () => {
    if (!noteBody.trim() || !customer) return;
    try {
      await api.post('/api/admin/crm/notes', { entity_type: 'user', entity_id: customer.user.user_id, body: noteBody });
      toast.success('Nota aggiunta');
      setNoteBody('');
      openCustomer(customer.user.user_id);
    } catch { toast.error('Errore'); }
  };

  const deleteNote = async (noteId) => {
    if (!window.confirm('Eliminare la nota?')) return;
    try {
      await api.delete(`/api/admin/crm/notes/${noteId}`);
      openCustomer(customer.user.user_id);
    } catch { toast.error('Errore'); }
  };

  const exportData = async (entity, format = 'csv') => {
    try {
      const r = await api.get(`/api/admin/crm/export/${entity}?format=${format}`, { responseType: 'blob' });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url; a.download = `${entity}.${format}`; a.click();
      setTimeout(() => URL.revokeObjectURL(url), 500);
      toast.success(`Export ${entity} (${format.toUpperCase()}) completato`);
    } catch { toast.error('Errore export'); }
  };

  const changeRequestStatus = async (requestId, newStatus) => {
    try {
      await api.put(`/api/admin/crm/requests/${requestId}`, { status: newStatus });
      toast.success('Stato aggiornato');
      const r = await api.get('/api/admin/crm/requests/board');
      setBoard(r.data);
      setReqEditor(null);
    } catch { toast.error('Errore aggiornamento'); }
  };

  // Drag & drop kanban — pointer-based (robusto, non dipende dal DnD nativo HTML5)
  const dragRef = useRef({ active: false, moved: false, requestId: null, sourceCol: null, card: null, startX: 0, startY: 0 });
  const [dragGhost, setDragGhost] = useState(null); // { label, x, y }

  const columnAtPoint = (x, y) => {
    const el = document.elementFromPoint(x, y);
    const colEl = el && el.closest ? el.closest('[data-col-key]') : null;
    return colEl ? colEl.getAttribute('data-col-key') : null;
  };

  const doMove = useCallback(async (requestId, sourceCol, targetCol, card) => {
    setBoard(prev => {
      const next = { ...prev };
      next[sourceCol] = (next[sourceCol] || []).filter(r => r.request_id !== requestId);
      next[targetCol] = [{ ...card, status: targetCol }, ...(next[targetCol] || [])];
      return next;
    });
    try {
      await api.put(`/api/admin/crm/requests/${requestId}`, { status: targetCol });
      toast.success('Stato aggiornato');
    } catch {
      toast.error('Errore aggiornamento — ricarico');
      const r = await api.get('/api/admin/crm/requests/board');
      setBoard(r.data);
    }
  }, []);

  const onWindowPointerMove = useCallback((e) => {
    const d = dragRef.current;
    if (!d.active) return;
    const dx = e.clientX - d.startX, dy = e.clientY - d.startY;
    if (!d.moved && Math.hypot(dx, dy) < 6) return;
    if (!d.moved) { d.moved = true; draggingRef.current = true; }
    e.preventDefault();
    setDragOverCol(columnAtPoint(e.clientX, e.clientY));
    setDragGhost({ label: d.card.description || 'Richiesta', x: e.clientX, y: e.clientY });
  }, []);

  const onWindowPointerUp = useCallback((e) => {
    window.removeEventListener('pointermove', onWindowPointerMove);
    window.removeEventListener('pointerup', onWindowPointerUp);
    const d = dragRef.current;
    dragRef.current = { active: false, moved: false, requestId: null, sourceCol: null, card: null, startX: 0, startY: 0 };
    draggingRef.current = false;
    setDragGhost(null);
    setDragOverCol(null);
    if (!d.active) return;
    if (!d.moved) { setReqEditor(d.card); return; } // click → apri editor
    const targetCol = columnAtPoint(e.clientX, e.clientY);
    if (targetCol && targetCol !== d.sourceCol) doMove(d.requestId, d.sourceCol, targetCol, d.card);
  }, [onWindowPointerMove, doMove]);

  const onCardPointerDown = useCallback((e, card, sourceCol) => {
    if (e.button !== undefined && e.button !== 0) return;
    dragRef.current = { active: true, moved: false, requestId: card.request_id, sourceCol, card, startX: e.clientX, startY: e.clientY };
    window.addEventListener('pointermove', onWindowPointerMove, { passive: false });
    window.addEventListener('pointerup', onWindowPointerUp);
  }, [onWindowPointerMove, onWindowPointerUp]);

  const assignProfessional = async (requestId, professionalId) => {
    setAssigning(true);
    try {
      await api.put(`/api/admin/crm/requests/${requestId}`, { assigned_professional_id: professionalId });
      const r = await api.get('/api/admin/crm/requests/board');
      setBoard(r.data);
      const updated = Object.values(r.data).flat().find(x => x.request_id === requestId);
      if (updated) setReqEditor(updated);
      toast.success('Richiesta assegnata e professionista notificato');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore assegnazione');
    } finally { setAssigning(false); }
  };

  const runAiMatch = async (requestId) => {
    setAiLoading(true);
    setAiMatches(null);
    try {
      const r = await api.post(`/api/admin/crm/requests/${requestId}/ai-match`);
      setAiMatches(r.data.matches || []);
      toast.success(`${(r.data.matches || []).length} match trovati`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore AI matching');
    } finally { setAiLoading(false); }
  };

  const importCsv = async (entity, file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append('file', file);
    setImportResult(null);
    try {
      const r = await api.post(`/api/admin/crm/import/${entity}`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setImportResult({ entity, ...r.data });
      toast.success(`Import completato: +${r.data.created} nuovi, ${r.data.updated} aggiornati`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore import');
    }
  };

  const sendMessage = async () => {
    if (!msgForm.to_user_id || !msgForm.body_html) { toast.error('Dati mancanti'); return; }
    try {
      await api.post('/api/admin/crm/messages', msgForm);
      toast.success('Messaggio inviato');
      setMsgOpen(false);
      setMsgForm({ to_user_id: '', subject: '', body_html: '', template: 'generic' });
      const r = await api.get('/api/admin/crm/messages?limit=30');
      setMessages(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || 'Errore invio'); }
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-background">
        <div className="flex items-center justify-center py-20">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-7xl mx-auto px-4 py-8">
        {!section && (
        <div className="mb-6 flex items-start justify-between flex-wrap gap-3">
          <div>
            <h1 className="font-heading font-semibold text-3xl" data-testid="crm-title">CRM VivaLink</h1>
            <p className="text-foreground/70 text-sm">Back-office completo — pipeline, pagamenti, clienti, comunicazioni</p>
          </div>
          <Button variant="outline" onClick={() => navigate('/admin')} data-testid="back-to-admin-btn">
            <ArrowRight size={16} className="mr-1 rotate-180" /> Admin Dashboard
          </Button>
        </div>
        )}

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          {!section && (
          <TabsList className="flex-wrap gap-2" data-testid="crm-tabs">
            <TabsTrigger value="pipeline" data-testid="tab-pipeline"><Kanban size={16} className="mr-1" />Pipeline</TabsTrigger>
            <TabsTrigger value="payments" data-testid="tab-payments"><CreditCard size={16} className="mr-1" />Pagamenti</TabsTrigger>
            <TabsTrigger value="customers" data-testid="tab-customers"><UserIcon size={16} className="mr-1" />Clienti 360°</TabsTrigger>
            <TabsTrigger value="search" data-testid="tab-search"><MagnifyingGlass size={16} className="mr-1" />Ricerca</TabsTrigger>
            <TabsTrigger value="messages" data-testid="tab-messages"><ChatCircle size={16} className="mr-1" />Messaggi</TabsTrigger>
            <TabsTrigger value="export" data-testid="tab-export"><DownloadSimple size={16} className="mr-1" />Import/Export</TabsTrigger>
          </TabsList>
          )}

          {/* PIPELINE / KANBAN */}
          <TabsContent value="pipeline" className="mt-6" data-testid="pipeline-tab-content">
            <div className="flex items-center justify-between gap-2 mb-3">
              <p className="text-xs text-foreground/60">Pipeline automatica: ogni richiesta si posiziona da sola nella colonna in base al suo stato attuale e si sposta automaticamente quando lo stato cambia. Puoi comunque trascinare una card per forzare manualmente lo stato, o cliccarla per aprire l&apos;editor con AI matching.</p>
              <span className="shrink-0 inline-flex items-center gap-1.5 text-xs font-medium text-emerald-600" data-testid="pipeline-live-indicator" title="La board si aggiorna automaticamente ogni 30 secondi">
                <span className="relative flex h-2 w-2">
                  <span className="absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75 animate-ping" />
                  <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-500" />
                </span>
                Live
              </span>
            </div>

            {/* Fase E: Geo filters */}
            <div className="flex flex-wrap gap-2 mb-4 items-end" data-testid="crm-geo-filters">
              <div>
                <label className="text-xs text-foreground/60 block mb-1">Regione</label>
                <Input placeholder="Es. Lombardia" value={geoFilter.region} onChange={(e) => setGeoFilter({ ...geoFilter, region: e.target.value })} className="w-40" data-testid="crm-filter-region" />
              </div>
              <div>
                <label className="text-xs text-foreground/60 block mb-1">Provincia</label>
                <Input placeholder="Es. Milano" value={geoFilter.province} onChange={(e) => setGeoFilter({ ...geoFilter, province: e.target.value })} className="w-40" data-testid="crm-filter-province" />
              </div>
              <div>
                <label className="text-xs text-foreground/60 block mb-1">Comune</label>
                <Input placeholder="Es. Milano" value={geoFilter.comune} onChange={(e) => setGeoFilter({ ...geoFilter, comune: e.target.value })} className="w-40" data-testid="crm-filter-comune" />
              </div>
              <Button size="sm" variant="ghost" className="rounded-full" disabled={!geoFilter.region && !geoFilter.province && !geoFilter.comune} onClick={() => setGeoFilter({ region: '', province: '', comune: '' })} data-testid="crm-clear-filters">Pulisci filtri</Button>
              <div className="text-xs text-foreground/50 ml-auto">
                {Object.values(board).flat().filter(matchGeo).length} richieste corrispondenti
              </div>
            </div>

            {/* Distance filter (uses geocoded pros + preset city coords) */}
            <div className="flex flex-wrap gap-3 mb-4 items-end p-3 rounded-xl bg-primary/5 border border-primary/20" data-testid="crm-distance-filter">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={distFilter.enabled}
                  onChange={(e) => setDistFilter({ ...distFilter, enabled: e.target.checked })}
                  className="w-4 h-4 accent-primary"
                  data-testid="crm-dist-toggle"
                />
                <span className="text-sm font-medium">Filtro distanza</span>
              </label>
              <div>
                <label className="text-xs text-foreground/60 block mb-1">Città di riferimento</label>
                <Input
                  list="crm-cities"
                  placeholder="Es. Milano"
                  value={distFilter.city}
                  onChange={(e) => setDistFilter({ ...distFilter, city: e.target.value })}
                  disabled={!distFilter.enabled}
                  className="w-48"
                  data-testid="crm-dist-city"
                />
                <datalist id="crm-cities">
                  <option value="Milano" /><option value="Roma" /><option value="Napoli" />
                  <option value="Torino" /><option value="Bologna" /><option value="Firenze" />
                  <option value="Genova" /><option value="Palermo" /><option value="Bari" />
                  <option value="Catania" /><option value="Venezia" /><option value="Verona" />
                  <option value="Padova" /><option value="Trieste" /><option value="Brescia" />
                  <option value="Parma" /><option value="Modena" /><option value="Reggio Emilia" />
                  <option value="Perugia" /><option value="Livorno" />
                </datalist>
              </div>
              <div>
                <label className="text-xs text-foreground/60 block mb-1">Raggio: {distFilter.radius_km} km</label>
                <input
                  type="range"
                  min="5"
                  max="100"
                  step="5"
                  value={distFilter.radius_km}
                  onChange={(e) => setDistFilter({ ...distFilter, radius_km: Number(e.target.value) })}
                  disabled={!distFilter.enabled}
                  className="w-48"
                  data-testid="crm-dist-radius"
                />
              </div>
              {distFilter.enabled && distFilter.city && (
                <div className="text-xs text-foreground/60">
                  {proximityPros.length} pro geolocalizzati entro il raggio
                </div>
              )}
            </div>

            <div className="flex gap-2">
              {COLUMNS.map(col => (
                <div
                  key={col.key}
                  className={`flex-1 min-w-0 rounded-xl border transition-colors ${dragOverCol === col.key ? 'bg-primary/10 ring-2 ring-primary/40 border-primary/30' : 'bg-[#F5FAFF] border-[#E3F2FD]'}`}
                  data-col-key={col.key}
                  data-testid={`col-${col.key}`}
                >
                  <div className="flex items-center justify-between gap-1 mb-3 px-2 pt-2">
                    <p className="font-heading font-medium text-sm truncate">{col.label}</p>
                    <Badge className={`${col.color} shrink-0`}>{(board[col.key] || []).filter(matchGeo).length}</Badge>
                  </div>
                  <div className="space-y-2 min-h-[120px] px-1.5 pb-2">
                    {(board[col.key] || []).filter(matchGeo).map(r => (
                      <Card
                        key={r.request_id}
                        className="p-2.5 cursor-grab active:cursor-grabbing hover:shadow-md transition-shadow select-none touch-none"
                        onPointerDown={(e) => onCardPointerDown(e, r, col.key)}
                        data-testid={`req-card-${r.request_id}`}
                      >
                        <p className="text-xs font-medium line-clamp-2">{r.description || 'Senza descrizione'}</p>
                        <p className="text-[11px] text-foreground/60 mt-1.5 truncate">{r.region || '—'} • {r.province || '—'}</p>
                        {r.assigned_professional_name ? (
                          <p className="text-[11px] mt-1.5 flex items-center gap-1 text-emerald-700 font-medium truncate" data-testid={`req-assignee-${r.request_id}`}>
                            <UserIcon size={11} weight="duotone" className="shrink-0" />
                            {r.accepted ? 'Accettata da' : 'Assegnata a'} {r.assigned_professional_name}
                          </p>
                        ) : r.notified_count > 0 ? (
                          <p className="text-[11px] mt-1.5 text-sky-700 truncate" data-testid={`req-notified-${r.request_id}`}>
                            {r.notified_count} professionist{r.notified_count === 1 ? 'a' : 'i'} segnalat{r.notified_count === 1 ? 'o' : 'i'}
                            {r.rotation_round > 0 ? ` · giro ${r.rotation_round + 1}` : ''}
                          </p>
                        ) : (
                          <p className="text-[11px] mt-1.5 text-foreground/40">Nessun professionista segnalato</p>
                        )}
                        <p className="text-[11px] text-foreground/50 mt-0.5">{new Date(r.created_at).toLocaleDateString('it-IT')}</p>
                      </Card>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            {dragGhost && (
              <div
                className="fixed z-[9999] pointer-events-none bg-white border border-primary/40 rounded-lg shadow-xl px-3 py-2 text-xs font-medium max-w-[220px] truncate"
                style={{ left: dragGhost.x + 12, top: dragGhost.y + 12 }}
                data-testid="drag-ghost"
              >
                {dragGhost.label}
              </div>
            )}

            {reqEditor && (
              <Dialog open={!!reqEditor} onOpenChange={(o) => { if (!o) { setReqEditor(null); setAiMatches(null); } }}>
                <DialogContent className="max-w-2xl">
                  <DialogHeader>
                    <DialogTitle>Gestione richiesta</DialogTitle>
                    <DialogDescription>Modifica stato o esegui AI matching per trovare i migliori professionisti.</DialogDescription>
                  </DialogHeader>
                  <div className="space-y-4">
                    <div className="bg-muted/40 rounded-lg p-3">
                      <p className="text-sm">{reqEditor.description}</p>
                      <p className="text-xs text-foreground/60 mt-1">{reqEditor.region} • {reqEditor.province} • urgenza: {reqEditor.urgency}</p>
                      <p className="text-xs mt-1">Stato attuale: <Badge>{reqEditor.status}</Badge></p>
                      {reqEditor.assigned_professional_name && (
                        <p className="text-xs mt-2 font-medium text-emerald-700" data-testid="editor-assignee">
                          {reqEditor.accepted ? 'Accettata da' : 'Assegnata a'}: {reqEditor.assigned_professional_name}
                        </p>
                      )}
                    </div>

                    <div>
                      <p className="text-xs font-medium mb-2">
                        Professionisti segnalati ({reqEditor.notified_count || 0}/5)
                        {reqEditor.rotation_round > 0 ? ` — giro ${reqEditor.rotation_round + 1}` : ''}
                      </p>
                      {(reqEditor.notified_professionals || []).length === 0 ? (
                        <p className="text-xs text-foreground/60" data-testid="no-notified-pros">
                          Nessun professionista segnalato: usa l'AI Matching qui sotto e assegna la richiesta.
                        </p>
                      ) : (
                        <div className="space-y-1.5" data-testid="notified-pros-list">
                          {reqEditor.notified_professionals.map(p => {
                            const isAssignee = p.professional_id === reqEditor.assigned_professional_id;
                            return (
                              <div key={p.professional_id} className="flex items-center justify-between gap-2 border border-border rounded-lg px-3 py-2" data-testid={`notified-pro-${p.professional_id}`}>
                                <span className="text-sm truncate">{p.name}</span>
                                {isAssignee ? (
                                  <Badge className="bg-emerald-100 text-emerald-800 shrink-0">{reqEditor.accepted ? 'Ha accettato' : 'Assegnato'}</Badge>
                                ) : (
                                  <Button size="sm" variant="outline" className="shrink-0" disabled={assigning}
                                    onClick={() => assignProfessional(reqEditor.request_id, p.professional_id)}
                                    data-testid={`assign-pro-${p.professional_id}`}>
                                    Assegna
                                  </Button>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>

                    <div>
                      <p className="text-xs font-medium mb-2">Cambia stato</p>
                      <div className="grid grid-cols-3 gap-2" data-testid="status-buttons">
                        {COLUMNS.map(c => (
                          <Button key={c.key} variant={c.key === reqEditor.status ? 'default' : 'outline'} size="sm"
                            onClick={() => changeRequestStatus(reqEditor.request_id, c.key)}
                            data-testid={`change-status-${c.key}`}>{c.label}</Button>
                        ))}
                      </div>
                    </div>

                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <p className="text-xs font-medium flex items-center gap-1"><Robot size={14} weight="duotone" /> AI Matching</p>
                        <Button size="sm" variant="outline" onClick={() => runAiMatch(reqEditor.request_id)} disabled={aiLoading} data-testid="ai-match-btn">
                          {aiLoading ? 'Analisi...' : 'Trova match'}
                        </Button>
                      </div>
                      {aiMatches && (
                        <div className="space-y-2 max-h-60 overflow-auto" data-testid="ai-matches-list">
                          {aiMatches.length === 0 ? (
                            <p className="text-xs text-foreground/60">Nessun match trovato</p>
                          ) : aiMatches.map((m, idx) => (
                            <div key={m.professional_id} className="border border-border rounded-lg p-3" data-testid={`ai-match-${idx}`}>
                              <div className="flex items-start justify-between gap-2">
                                <div className="min-w-0">
                                  <p className="text-sm font-medium">{m.name}</p>
                                  <p className="text-xs text-foreground/60">{m.location} • €{m.hourly_rate}/h</p>
                                </div>
                                <Badge className="bg-primary text-white flex items-center gap-1"><Star size={10} weight="fill" />{m.match_score}</Badge>
                              </div>
                              <p className="text-xs mt-2 text-foreground/70 italic">{m.reason}</p>
                              <Button size="sm" variant="outline" className="w-full mt-2" disabled={assigning}
                                onClick={() => assignProfessional(reqEditor.request_id, m.professional_id)}
                                data-testid={`ai-assign-${m.professional_id}`}>
                                {m.professional_id === reqEditor.assigned_professional_id ? 'Già assegnato' : 'Assegna a ' + m.name}
                              </Button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </DialogContent>
              </Dialog>
            )}
          </TabsContent>

          {/* PAYMENTS */}
          <TabsContent value="payments" className="mt-6" data-testid="payments-tab-content">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
              <Card className="p-4"><p className="text-xs text-foreground/60 uppercase">Transazioni</p><p className="text-3xl font-heading font-bold">{payments.stats?.total || 0}</p></Card>
              <Card className="p-4"><p className="text-xs text-foreground/60 uppercase">Pagate</p><p className="text-3xl font-heading font-bold">{payments.stats?.paid || 0}</p></Card>
              <Card className="p-4"><p className="text-xs text-foreground/60 uppercase">Revenue</p><p className="text-3xl font-heading font-bold">€{payments.stats?.revenue_eur || 0}</p></Card>
            </div>
            <Card>
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="payments-table">
                  <thead className="bg-muted/50">
                    <tr>
                      <th className="text-left p-3">Data</th><th className="text-left p-3">User</th>
                      <th className="text-left p-3">Piano</th><th className="text-left p-3">Importo</th>
                      <th className="text-left p-3">Stato</th>
                    </tr>
                  </thead>
                  <tbody>
                    {payments.items.map(p => (
                      <tr key={p.session_id} className="border-t border-border" data-testid={`payment-${p.session_id}`}>
                        <td className="p-3 text-xs">{new Date(p.created_at).toLocaleString('it-IT')}</td>
                        <td className="p-3 text-xs">{p.user_id}</td>
                        <td className="p-3 text-xs">{p.lookup_key || p.plan || '—'}</td>
                        <td className="p-3 text-xs">€{((p.amount || 0) / 100).toFixed(2)}</td>
                        <td className="p-3"><Badge className={p.payment_status === 'paid' ? 'bg-sky-100 text-sky-800' : 'bg-yellow-100 text-yellow-800'}>{p.payment_status}</Badge></td>
                      </tr>
                    ))}
                    {payments.items.length === 0 && <tr><td colSpan="5" className="p-8 text-center text-foreground/60">Nessun pagamento</td></tr>}
                  </tbody>
                </table>
              </div>
            </Card>
          </TabsContent>

          {/* CUSTOMER 360 */}
          <TabsContent value="customers" className="mt-6" data-testid="customers-tab-content">
            {customersList.length > 0 && (
              <Card className="p-4 mb-4" data-testid="customers-list-card">
                <p className="font-heading font-medium mb-3">Tutti i clienti ({customersList.length})</p>
                <div className="overflow-x-auto max-h-[520px] overflow-y-auto">
                  <table className="w-full text-sm" data-testid="customers-table">
                    <thead className="bg-muted/50 sticky top-0">
                      <tr>
                        <th className="text-left p-2">Cliente</th>
                        <th className="text-left p-2">Email</th>
                        <th className="text-center p-2">Richieste</th>
                        <th className="text-center p-2">Completate</th>
                        <th className="text-left p-2"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {customersList.map((c) => (
                        <tr key={c.user_id} className="border-t border-border hover:bg-muted/30" data-testid={`customer-row-${c.user_id}`}>
                          <td className="p-2 font-medium">{c.name || '—'}</td>
                          <td className="p-2 text-xs text-foreground/70">{c.email}</td>
                          <td className="p-2 text-center">
                            <Badge variant="outline" data-testid={`customer-requests-${c.user_id}`}>{c.requests_count}</Badge>
                          </td>
                          <td className="p-2 text-center">
                            {completedCount(c.status_counts) > 0
                              ? <Badge className="bg-emerald-100 text-emerald-800" data-testid={`customer-completed-${c.user_id}`}>{completedCount(c.status_counts)}</Badge>
                              : <span className="text-xs text-foreground/40">0</span>}
                          </td>
                          <td className="p-2 text-right whitespace-nowrap">
                            <Button size="sm" variant="ghost" onClick={() => openCustomer(c.user_id)} data-testid={`open-customer-${c.user_id}`}>
                              Apri scheda <ArrowRight size={14} className="ml-1" />
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              className="text-destructive hover:bg-destructive/10"
                              disabled={deletingCustomerId === c.user_id}
                              onClick={() => deleteCustomer(c)}
                              data-testid={`delete-customer-${c.user_id}`}
                              title="Elimina cliente"
                            >
                              <Trash size={16} />
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </Card>
            )}
            <div className="flex gap-3 mb-4">
              <Input placeholder="user_id (es. user_test001)" value={selectedUserId} onChange={e => setSelectedUserId(e.target.value)} data-testid="customer-userid-input" />
              <Button onClick={() => openCustomer(selectedUserId)} data-testid="open-customer-btn">Apri</Button>
            </div>
            <Dialog open={!!customer} onOpenChange={(o) => { if (!o) setCustomer(null); }}>
              <DialogContent className="max-w-3xl max-h-[88vh] overflow-y-auto" data-testid="customer-detail-dialog">
                {customer && (
                <div className="space-y-4">
                  <DialogHeader>
                    <DialogTitle data-testid="customer-name">{customer.user.name}</DialogTitle>
                    <DialogDescription>{customer.user.email} • {customer.user.role}</DialogDescription>
                  </DialogHeader>
                  <Card className="p-5">
                    <div className="grid grid-cols-4 gap-3 text-center">
                      <div><p className="text-2xl font-bold">{customer.kpi.bookings_count}</p><p className="text-xs">Booking</p></div>
                      <div><p className="text-2xl font-bold">{customer.kpi.requests_count}</p><p className="text-xs">Richieste</p></div>
                      <div><p className="text-2xl font-bold">{customer.kpi.payments_count}</p><p className="text-xs">Pagamenti</p></div>
                      <div><p className="text-2xl font-bold">€{customer.kpi.revenue_paid_eur}</p><p className="text-xs">Revenue</p></div>
                    </div>
                  </Card>

                <Card className="p-5">
                  <p className="font-heading font-medium mb-3 flex items-center gap-2"><Note size={18} weight="duotone" /> Note interne</p>
                  <div className="flex gap-2 mb-3">
                    <Textarea rows={2} value={noteBody} onChange={e => setNoteBody(e.target.value)} placeholder="Aggiungi nota interna..." data-testid="note-input" />
                    <Button onClick={addNote} data-testid="add-note-btn"><Plus size={16} /></Button>
                  </div>
                  <div className="space-y-2">
                    {customer.notes.map(n => (
                      <div key={n.note_id} className="border-l-2 border-primary/40 pl-3 py-1 flex justify-between" data-testid={`note-${n.note_id}`}>
                        <div>
                          <p className="text-sm">{n.body}</p>
                          <p className="text-xs text-foreground/50">{n.author_name} • {new Date(n.created_at).toLocaleString('it-IT')}</p>
                        </div>
                        <Button size="sm" variant="ghost" onClick={() => deleteNote(n.note_id)}><Trash size={14} /></Button>
                      </div>
                    ))}
                    {customer.notes.length === 0 && <p className="text-xs text-foreground/60">Nessuna nota</p>}
                  </div>
                </Card>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Card className="p-5">
                    <p className="font-heading font-medium mb-2">
                      Richieste ({customer.requests.length}
                      {customer.requests.filter(r => isCompletedStatus(r.status)).length > 0
                        ? ` · ${customer.requests.filter(r => isCompletedStatus(r.status)).length} completate`
                        : ''})
                    </p>
                    <div className="space-y-1 max-h-64 overflow-auto" data-testid="customer-requests-list">
                      {customer.requests.length === 0 && <p className="text-xs text-foreground/60">Nessuna richiesta</p>}
                      {customer.requests.map(r => (
                        <div key={r.request_id} className="text-xs border-b border-border py-2 flex items-start justify-between gap-2" data-testid={`customer-req-${r.request_id}`}>
                          <div className="min-w-0">
                            <p className="font-medium truncate">{r.description || 'Senza descrizione'}</p>
                            <p className="text-foreground/50">{new Date(r.created_at).toLocaleDateString('it-IT')}</p>
                          </div>
                          <Badge className={`shrink-0 ${isCompletedStatus(r.status) ? 'bg-emerald-100 text-emerald-800' : 'bg-sky-100 text-sky-800'}`}>
                            {statusLabel(r.status)}
                          </Badge>
                        </div>
                      ))}
                    </div>
                  </Card>
                  <Card className="p-5">
                    <p className="font-heading font-medium mb-2">Ultimi pagamenti ({customer.payments.length})</p>
                    <div className="space-y-1 max-h-64 overflow-auto">
                      {customer.payments.slice(0, 10).map(p => (
                        <div key={p.session_id} className="text-xs border-b border-border py-2 flex justify-between">
                          <span>{p.lookup_key} — €{((p.amount || 0) / 100).toFixed(2)}</span>
                          <Badge className="text-xs">{p.payment_status}</Badge>
                        </div>
                      ))}
                    </div>
                  </Card>
                </div>

                <Button className="w-full" onClick={() => { setMsgForm({ ...msgForm, to_user_id: customer.user.user_id }); setMsgOpen(true); }} data-testid="msg-customer-btn">
                  <ChatCircle size={16} className="mr-2" /> Invia messaggio a {customer.user.name}
                </Button>
                <Button
                  variant="outline"
                  className="w-full border-destructive text-destructive hover:bg-destructive/10"
                  disabled={clearingHistory}
                  onClick={clearCustomerHistory}
                  data-testid="clear-customer-history-btn"
                >
                  <Trash size={16} className="mr-2" /> Svuota storico (richieste, prenotazioni, pagamenti non incassati)
                </Button>
                </div>
                )}
              </DialogContent>
            </Dialog>
          </TabsContent>

          {/* SEARCH */}
          <TabsContent value="search" className="mt-6" data-testid="search-tab-content">
            <div className="mb-4">
              <Input placeholder="Cerca utenti, professionisti, centri, richieste..." value={searchQ} onChange={e => setSearchQ(e.target.value)} data-testid="global-search-input" />
              <p className="text-xs text-foreground/60 mt-1">Min. 2 caratteri • ricerca live</p>
            </div>
            {searchResults && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {[
                  { key: 'users', label: 'Utenti', items: searchResults.users, id: 'user_id' },
                  { key: 'professionals', label: 'Professionisti', items: searchResults.professionals, id: 'professional_id' },
                  { key: 'centers', label: 'Centri', items: searchResults.centers, id: 'center_id' },
                  { key: 'requests', label: 'Richieste', items: searchResults.requests, id: 'request_id' },
                ].map(sec => (
                  <Card key={sec.key} className="p-4" data-testid={`search-section-${sec.key}`}>
                    <p className="font-heading font-medium mb-2">{sec.label} ({sec.items.length})</p>
                    <div className="space-y-1 max-h-60 overflow-auto">
                      {sec.items.map(it => (
                        <div key={it[sec.id]} className="text-xs border-b border-border py-2">
                          <p className="font-medium">{it.name || it.email || it.description?.slice(0, 60) || it[sec.id]}</p>
                          <p className="text-foreground/60">{it.email || it.region || it.status || it[sec.id]}</p>
                        </div>
                      ))}
                      {sec.items.length === 0 && <p className="text-xs text-foreground/60">Nessun risultato</p>}
                    </div>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* MESSAGES */}
          <TabsContent value="messages" className="mt-6" data-testid="messages-tab-content">
            <div className="mb-4 flex justify-end">
              <Dialog open={msgOpen} onOpenChange={setMsgOpen}>
                <DialogTrigger asChild>
                  <Button data-testid="new-message-btn"><Plus size={16} className="mr-1" /> Nuovo messaggio</Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Nuovo messaggio</DialogTitle>
                    <DialogDescription>Invia una email templated al destinatario. Il body è escaped automaticamente per prevenire injection.</DialogDescription>
                  </DialogHeader>
                  <div className="space-y-3">
                    <Input placeholder="to_user_id (es. user_test001)" value={msgForm.to_user_id} onChange={e => setMsgForm({ ...msgForm, to_user_id: e.target.value })} data-testid="msg-to-input" />
                    <Select value={msgForm.template} onValueChange={v => setMsgForm({ ...msgForm, template: v })}>
                      <SelectTrigger data-testid="msg-template-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="generic">Generico</SelectItem>
                        <SelectItem value="follow_up">Follow-up richiesta</SelectItem>
                        <SelectItem value="reminder">Promemoria</SelectItem>
                      </SelectContent>
                    </Select>
                    <Input placeholder="Oggetto (opzionale)" value={msgForm.subject} onChange={e => setMsgForm({ ...msgForm, subject: e.target.value })} data-testid="msg-subject-input" />
                    <Textarea rows={4} placeholder="Corpo del messaggio" value={msgForm.body_html} onChange={e => setMsgForm({ ...msgForm, body_html: e.target.value })} data-testid="msg-body-input" />
                  </div>
                  <DialogFooter>
                    <Button variant="outline" onClick={() => setMsgOpen(false)}>Annulla</Button>
                    <Button onClick={sendMessage} data-testid="send-msg-btn">Invia</Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </div>
            <Card>
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="messages-table">
                  <thead className="bg-muted/50">
                    <tr>
                      <th className="text-left p-3">Data</th><th className="text-left p-3">Destinatario</th>
                      <th className="text-left p-3">Oggetto</th><th className="text-left p-3">Template</th>
                      <th className="text-left p-3">Stato</th>
                    </tr>
                  </thead>
                  <tbody>
                    {messages.map(m => (
                      <tr key={m.message_id} className="border-t border-border">
                        <td className="p-3 text-xs">{new Date(m.created_at).toLocaleString('it-IT')}</td>
                        <td className="p-3 text-xs">{m.to_email_masked}</td>
                        <td className="p-3 text-xs">{m.subject}</td>
                        <td className="p-3 text-xs">{m.template || 'generic'}</td>
                        <td className="p-3"><Badge>{m.status}</Badge></td>
                      </tr>
                    ))}
                    {messages.length === 0 && <tr><td colSpan="5" className="p-8 text-center text-foreground/60">Nessun messaggio inviato</td></tr>}
                  </tbody>
                </table>
              </div>
            </Card>
          </TabsContent>

          {/* EXPORT */}
          <TabsContent value="export" className="mt-6" data-testid="export-tab-content">
            <div className="space-y-4">
              <Card className="p-6">
                <p className="font-heading font-medium mb-2 flex items-center gap-2"><DownloadSimple size={18} weight="duotone" /> Export dati</p>
                <p className="text-sm text-foreground/70 mb-4">Scarica i dati di una entità in <strong>CSV</strong>, <strong>Excel</strong> o <strong>PDF</strong> (max 5.000 righe).</p>
                <div className="space-y-3">
                  {['users', 'professionals', 'requests', 'bookings', 'payments', 'centers'].map(e => (
                    <div key={e} className="flex items-center gap-2 flex-wrap border-b border-border pb-2 last:border-0">
                      <span className="w-28 text-sm font-medium capitalize">{e}</span>
                      <Button size="sm" variant="outline" onClick={() => exportData(e, 'csv')} data-testid={`export-${e}-csv`}>
                        <DownloadSimple size={14} className="mr-1" /> CSV
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => exportData(e, 'xlsx')} data-testid={`export-${e}-xlsx`}>
                        <DownloadSimple size={14} className="mr-1" /> Excel
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => exportData(e, 'pdf')} data-testid={`export-${e}-pdf`}>
                        <DownloadSimple size={14} className="mr-1" /> PDF
                      </Button>
                    </div>
                  ))}
                </div>
              </Card>

              <Card className="p-6">
                <p className="font-heading font-medium mb-2 flex items-center gap-2"><UploadSimple size={18} weight="duotone" /> Import CSV</p>
                <p className="text-sm text-foreground/70 mb-4">Bulk onboarding di Centri e Zone. Max 1000 righe, 2MB per file.</p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="border border-dashed border-border rounded-lg p-4">
                    <p className="text-sm font-medium mb-1">Centri</p>
                    <p className="text-xs text-foreground/60 mb-3">Colonne: <code className="text-xs">name*, region*, province*, city, address, phone, email, description, active</code></p>
                    <label className="inline-block">
                      <input type="file" accept=".csv" hidden onChange={e => importCsv('centers', e.target.files?.[0])} data-testid="import-centers-input" />
                      <Button variant="outline" size="sm" asChild data-testid="import-centers-btn">
                        <span className="cursor-pointer"><UploadSimple size={16} className="mr-1" /> Carica CSV Centri</span>
                      </Button>
                    </label>
                  </div>
                  <div className="border border-dashed border-border rounded-lg p-4">
                    <p className="text-sm font-medium mb-1">Zone</p>
                    <p className="text-xs text-foreground/60 mb-3">Colonne: <code className="text-xs">region*, province*, enabled</code></p>
                    <label className="inline-block">
                      <input type="file" accept=".csv" hidden onChange={e => importCsv('zones', e.target.files?.[0])} data-testid="import-zones-input" />
                      <Button variant="outline" size="sm" asChild data-testid="import-zones-btn">
                        <span className="cursor-pointer"><UploadSimple size={16} className="mr-1" /> Carica CSV Zone</span>
                      </Button>
                    </label>
                  </div>
                </div>
                {importResult && (
                  <div className="mt-4 bg-muted/40 rounded-lg p-3 text-sm" data-testid="import-result">
                    <p className="font-medium mb-1">Risultato import {importResult.entity}:</p>
                    <p className="text-xs">Righe: {importResult.total_rows} • Creati: {importResult.created} • Aggiornati: {importResult.updated} • Errori: {(importResult.errors || []).length}</p>
                    {(importResult.errors || []).length > 0 && (
                      <details className="mt-2">
                        <summary className="text-xs cursor-pointer">Vedi errori</summary>
                        <div className="text-xs mt-1 max-h-32 overflow-auto">
                          {importResult.errors.map((e, i) => <p key={i}>Riga {e.row}: {e.error}</p>)}
                        </div>
                      </details>
                    )}
                  </div>
                )}
              </Card>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default CrmDashboard;
